import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { SecretInput } from "@/components/design/SecretInput";
import { Select } from "@/components/design/Select";
import {
  ModelConfigError,
  loadModelConfig,
  saveModelConfig,
  type WebSearchSetting,
  type WebSearchTool,
} from "@/lib/model-config";
import { LocalizedError } from "@/lib/localized-error";
import { usePendingScope } from "@/lib/pending-edits";
import { SettingsCard } from "./SettingsCard";
import { KnowledgeDocumentsCard } from "./KnowledgeDocumentsCard";
import { ResearchSourcesCard } from "./ResearchSourcesCard";
import { modelConfigSaveErrorMessage } from "./model-config-save-error";
// Official Mistral model icons (downloaded from mistral.ai/brand, used as-is).
import mistralLarge from "@/assets/providers/mistral/mistral-large.svg";
import mistralSmall from "@/assets/providers/mistral/mistral-small.svg";
import mistralOcr from "@/assets/providers/mistral/mistral-ocr.svg";
import mistralImage from "@/assets/providers/mistral/mistral-image.svg";
import mistralEmbedding from "@/assets/providers/mistral/mistral-embedding.svg";

// AI Model panel (Settings · AI Model). Pick the upstream model provider, supply each provider's API
// key (password-masked) and set its web-search switch. Edits register with the app-wide SaveBar
// (@adr SPARK-0005-unified-deferred-save-bar@1): one Speichern/Verwerfen for the page, and the same
// registry holds a section switch or a navigation while something here is unsaved. Saving POSTs to the
// BFF (/ai/model-config), which writes provider, non-empty keys and the switch into gemstone;
// model-gateway reads them back (and hot-reloads). Admin-only on the BFF. The provider names, API-key
// env vars and resolved model ids mirror model-gateway config/default.toml.

type Provider = "mistral" | "openai" | "anthropic";

const PROVIDERS = [
  { value: "mistral", label: "Mistral AI" },
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
] as const;

interface ProviderInfo {
  envVar: string;
  keyPlaceholder: string;
  models: { kind: string; id: string }[];
  // The hosted web search this provider can run (model-gateway `[providers.<name>.web_search]`), with
  // the connectors the admin may pick. Absent = the gateway has no search adapter for this provider.
  webSearch?: { tools: WebSearchTool[] };
}

// The admin's three-way choice for a provider that can search: off, or on with one of its connectors.
type WebSearchMode = "off" | WebSearchTool;

const WEB_SEARCH_DEFAULT: WebSearchSetting = { enabled: false, tool: "web_search" };
const EMPTY_KEYS: Record<Provider, string> = { mistral: "", openai: "", anthropic: "" };
const DEFAULT_WEB_SEARCH: Record<Provider, WebSearchSetting> = {
  mistral: WEB_SEARCH_DEFAULT,
  openai: WEB_SEARCH_DEFAULT,
  anthropic: WEB_SEARCH_DEFAULT,
};

// What gemstone holds as of the last load or save: the baseline the draft is judged dirty against and
// the state Verwerfen returns to. Keys hold the stored (decrypted) values the fields were pre-filled with.
interface StoredConfig {
  provider: Provider;
  keys: Record<Provider, string>;
  webSearch: Record<Provider, WebSearchSetting>;
}


function webSearchMode(setting: WebSearchSetting): WebSearchMode {
  return setting.enabled ? setting.tool : "off";
}

function withWebSearchMode(setting: WebSearchSetting, mode: WebSearchMode): WebSearchSetting {
  // Switching off keeps the connector, so switching back on lands on the same one.
  return mode === "off" ? { ...setting, enabled: false } : { enabled: true, tool: mode };
}

const PROVIDER_INFO: Record<Provider, ProviderInfo> = {
  mistral: {
    envVar: "MODEL_GATEWAY_MISTRAL__API_KEY",
    keyPlaceholder: "your Mistral API key",
    models: [
      { kind: "small", id: "mistral-small-latest" },
      { kind: "large", id: "mistral-large-latest" },
      // Pixtral is deprecated — vision is folded into Large 3.
      { kind: "image", id: "mistral-large-latest" },
      { kind: "embedding", id: "mistral-embed" },
      { kind: "ocr", id: "mistral-ocr-latest" },
      { kind: "thinking", id: "magistral-medium-latest" },
    ],
    // Mistral Conversations API connectors; `web_search_premium` adds verified news sources.
    webSearch: { tools: ["web_search", "web_search_premium"] },
  },
  openai: {
    envVar: "MODEL_GATEWAY_OPENAI__API_KEY",
    keyPlaceholder: "sk-…",
    models: [
      { kind: "small", id: "gpt-5.6-luna" },
      { kind: "large", id: "gpt-5.6-sol" },
      { kind: "image", id: "gpt-5.6-sol" },
      { kind: "embedding", id: "text-embedding-3-small" },
      // No `ocr` row: the gateway defines no ocr kind for openai.
      { kind: "thinking", id: "gpt-5.6-terra" },
    ],
  },
  anthropic: {
    envVar: "MODEL_GATEWAY_ANTHROPIC__API_KEY",
    keyPlaceholder: "sk-ant-…",
    models: [
      { kind: "small", id: "claude-haiku-4-5" },
      { kind: "large", id: "claude-opus-5" },
      { kind: "image", id: "claude-opus-5" },
      // Anthropic has no first-party embedding model; Voyage AI is its recommended partner
      // (not served by the api.anthropic.com endpoint — placeholder for the mock).
      { kind: "embedding", id: "voyage-3-large" },
      // No dedicated OCR endpoint — OCR runs through the vision model (claude-opus-5).
      { kind: "ocr", id: "claude-opus-5" },
      { kind: "thinking", id: "claude-opus-5" },
    ],
  },
};

// The providers whose tile carries the web-search dropdown; only these are ever saved with a switch.
const SEARCHABLE_PROVIDERS = PROVIDERS.filter((p) => PROVIDER_INFO[p.value].webSearch).map((p) => p.value);

// Model-kind → official Mistral icon. Only Mistral ships per-model icons; other
// providers fall back to no icon.
const MISTRAL_MODEL_ICONS: Record<string, string> = {
  large: mistralLarge,
  small: mistralSmall,
  ocr: mistralOcr,
  image: mistralImage,
  embedding: mistralEmbedding,
};

// Optional per-provider brand logo, dropped into
// assets/providers/<provider>/logo.svg. Absent → the tile shows the name text.
const PROVIDER_LOGOS = import.meta.glob("../../assets/providers/*/logo.svg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;

function providerLogo(value: string): string | undefined {
  const match = Object.entries(PROVIDER_LOGOS).find(([path]) =>
    path.includes(`/providers/${value}/logo.svg`),
  );
  return match?.[1];
}

export function ModelPanel() {
  const intl = useIntl();
  const [provider, setProvider] = useState<Provider>("mistral");
  const [keys, setKeys] = useState<Record<Provider, string>>(EMPTY_KEYS);
  // The web-search switch per provider (only providers with a search adapter are ever saved). Starts at
  // the gateway's default, off, until the stored state arrives.
  const [webSearch, setWebSearch] = useState<Record<Provider, WebSearchSetting>>(DEFAULT_WEB_SEARCH);
  // The stored baseline (see StoredConfig). Its provider is the ACTIVE one, what the backend embeds
  // against; the selected tile diverges from it the moment another tile is clicked, before Save — that
  // gap is what the switch hint below keys off.
  const [stored, setStored] = useState<StoredConfig>({
    provider: "mistral",
    keys: EMPTY_KEYS,
    webSearch: DEFAULT_WEB_SEARCH,
  });
  const savedProvider = stored.provider;
  const [confirmation, setConfirmation] = useState(false);
  // Bumped on a successful save so KnowledgeDocumentsCard reloads and re-derives per-provider availability.
  const [reembedRefresh, setReembedRefresh] = useState(0);
  // After a provider change, uploads are blocked until this timestamp: model-gateway can take ~30s to
  // pick the switch up from gemstone, and the ingestion worker embeds against whatever provider the
  // gateway reports at processing time — so an immediate upload would embed for the OLD provider.
  const [uploadBlockedUntil, setUploadBlockedUntil] = useState<number | null>(null);

  // Load the persisted state so the active provider + stored keys survive a refresh. A failed load
  // (not admin / gemstone down) just leaves the defaults — the settings page still renders.
  useEffect(() => {
    let cancelled = false;
    loadModelConfig()
      .then((state) => {
        if (cancelled) return;
        const loaded: StoredConfig = {
          provider: state.provider ?? "mistral",
          keys: { ...EMPTY_KEYS, ...state.keys },
          webSearch: { ...DEFAULT_WEB_SEARCH, ...state.webSearch },
        };
        setStored(loaded);
        // The draft adopts the stored state only where the reader has not edited yet: the load lands
        // after mount, and an edit made in the meantime must survive it (as an unsaved edit).
        setProvider((current) => (current === "mistral" ? loaded.provider : current));
        setKeys((current) =>
          Object.fromEntries(
            PROVIDERS.map((p) => [p.value, current[p.value] !== "" ? current[p.value] : loaded.keys[p.value]]),
          ) as Record<Provider, string>,
        );
        setWebSearch((current) =>
          Object.fromEntries(
            PROVIDERS.map((p) => [
              p.value,
              current[p.value] === WEB_SEARCH_DEFAULT ? loaded.webSearch[p.value] : current[p.value],
            ]),
          ) as Record<Provider, WebSearchSetting>,
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // One persist path for every caller — the SaveBar, the unsaved-changes dialog and the settings
  // page's section gate all run this through the pending scope below. A refusal travels as a
  // LocalizedError so those surfaces can show what to fix (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
  const persist = async () => {
    // Only providers that can search carry a switch; the others have nothing to store.
    const webSearchToSave = Object.fromEntries(
      SEARCHABLE_PROVIDERS.map((p) => [p, webSearch[p]]),
    ) as Partial<Record<Provider, WebSearchSetting>>;
    try {
      await saveModelConfig({ provider, keys, webSearch: webSearchToSave });
    } catch (e) {
      // ModelConfigError carries the HTTP status; a transport failure (no response) → status 0 → generic.
      const status = e instanceof ModelConfigError ? e.status : 0;
      throw new LocalizedError(modelConfigSaveErrorMessage(intl, status), { cause: e });
    }
    if (provider !== stored.provider) setUploadBlockedUntil(Date.now() + 30_000);
    setStored({ provider, keys, webSearch });
    setReembedRefresh((n) => n + 1);
    setConfirmation(true);
    // Longer than a bare "saved" toast: the message mentions the propagation delay, so give the reader
    // time to read it.
    window.setTimeout(() => setConfirmation(false), 5000);
  };

  const discard = () => {
    setProvider(stored.provider);
    setKeys(stored.keys);
    setWebSearch(stored.webSearch);
  };

  // Dirty = the draft differs from the stored baseline in any of the three things this card owns. A key
  // counts as edited the moment its text differs from the pre-filled one (a blank field is "leave
  // unchanged" on the wire, but typing over a stored key is still an edit the reader would lose).
  const dirty =
    provider !== stored.provider ||
    PROVIDERS.some((p) => keys[p.value] !== stored.keys[p.value]) ||
    SEARCHABLE_PROVIDERS.some(
      (p) =>
        webSearch[p].enabled !== stored.webSearch[p].enabled || webSearch[p].tool !== stored.webSearch[p].tool,
    );
  usePendingScope("model-config", { dirty, save: persist, discard });

  // A different tile means a different embedding model; uploaded docs stay embedded for the saved
  // provider until re-embedded (spec §8/§11), so name the change so the admin knows to re-embed.
  const embedModelId = (p: Provider) => PROVIDER_INFO[p].models.find((m) => m.kind === "embedding")?.id ?? "—";
  const switching = provider !== savedProvider;

  return (
    <>
      <SettingsCard
        title={intl.formatMessage({ id: "model.providerTitle", defaultMessage: "Provider" })}
        sub={intl.formatMessage({
          id: "model.providerSub",
          defaultMessage: "The upstream model stack. Model kinds resolve to this provider's concrete models.",
        })}
      >
        {/* Provider as selectable tiles. The active tile carries the accent
            ring, a breathing live dot, a "Connected" line and its API-key
            field, and grows downward to reveal them (top edge fixed via
            items-start). The key input can't sit inside the <button> (no nested
            interactive elements), so the header is the button and the key + env
            var render as siblings below it. */}
        {/* min-height >= the tallest active tile (key + full model stack) so the
            row height never changes when switching provider: the collapsing and
            expanding tiles animate inside a constant-height row, so the Provider
            card doesn't shrink/grow (no jitter). */}
        {/* min-height sized for the tallest active tile at 6 model rows (thinking added). */}
        <div className="grid grid-cols-1 items-start gap-3 @sm:grid-cols-3 sm:min-h-[23rem]">
          {PROVIDERS.map((p) => {
            const on = p.value === provider;
            // "Connected" = this provider has a key (loaded from gemstone or just typed).
            const connected = keys[p.value].trim().length > 0;
            const pInfo = PROVIDER_INFO[p.value];
            return (
              <div
                key={p.value}
                className={`box overflow-hidden transition ${on ? "bg-ink/[0.06] ring-accent-decoration" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => setProvider(p.value)}
                  aria-pressed={on}
                  aria-label={p.label}
                  aria-description={connected ? intl.formatMessage({ id: "model.connected" }) : undefined}
                  className={`flex w-full items-center justify-between gap-2 p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink-ui ${
                    on ? "" : "hover:bg-ink/[0.06]"
                  }`}
                >
                  <span className={`text-smycles-body ${connected ? "font-semibold" : "font-medium"}`}>{p.label}</span>
                  {connected ? <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-accent pulse-dot" aria-hidden="true" /> : null}
                </button>
                {/* Connected + API key collapse via grid-rows 0fr→1fr so the tile
                    grows/shrinks smoothly; kept mounted so the open animates. */}
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
                    on ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div
                    className={`overflow-hidden transition-opacity duration-300 motion-reduce:transition-none ${
                      on ? "opacity-100" : "opacity-0 invisible"
                    }`}
                  >
                    <div className="px-4 pb-4">

                      <SecretInput
                        id={`model-key-${p.value}`}
                        value={keys[p.value]}
                        onChange={(v) => setKeys((k) => ({ ...k, [p.value]: v }))}
                        ariaLabel={intl.formatMessage(
                          { id: "model.keyAriaLabel", defaultMessage: "{provider} API key" },
                          { provider: p.label },
                        )}
                        placeholder={pInfo.keyPlaceholder}
                      />
                      <div className="smycles-field-meta mt-1 font-mono text-ink-muted">{pInfo.envVar}</div>

                      {/* This provider's resolved model stack lives in its own
                          box. Keyed by `on` so the staggered reveal replays each
                          time the box opens. Mistral ships per-model icons;
                          OpenAI/Anthropic reuse their provider logo. Brand marks
                          stay in their brand colors by requirement — an
                          intentional exception to the monochrome palette. */}
                      <div className="smycles-field-label mb-2 mt-4">
                        {intl.formatMessage({ id: "model.resolvedModels", defaultMessage: "Resolved models" })}
                      </div>
                      <div key={on ? "open" : "closed"} className="space-y-1.5">
                        {pInfo.models.map((m, i) => {
                          const icon =
                            p.value === "mistral" ? MISTRAL_MODEL_ICONS[m.kind] : providerLogo(p.value);
                          return (
                            <div key={m.kind} className="flex items-center justify-between gap-4">
                              <span className="smycles-field-label">{m.kind === "ocr" ? "OCR" : m.kind}</span>
                              <span
                                className="reveal-rise smycles-field-value flex items-center gap-2 font-mono text-ink"
                                style={{ animationDelay: `${i * 110}ms` }}
                              >
                                {icon ? <img src={icon} alt="" aria-hidden className="h-5 w-5 shrink-0" /> : null}
                                <span className="reveal-wipe" style={{ animationDelay: `${i * 110 + 90}ms` }}>
                                  {m.id}
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* The web-search switch: off, or on with one of the provider's connectors, as one
                          dropdown (a Select, the standalone `box` variant). A provider without a search
                          adapter says so instead of showing a dead control. */}
                      {pInfo.webSearch ? (
                        <div data-testid={`model-web-search-${p.value}`}>
                          <label htmlFor={`model-web-search-${p.value}`} className="smycles-field-label mb-2 mt-4 block">
                            {intl.formatMessage({ id: "model.webSearch.label", defaultMessage: "Web search" })}
                          </label>
                          <Select
                            id={`model-web-search-${p.value}`}
                            hideEmpty
                            value={webSearchMode(webSearch[p.value])}
                            onChange={(next) => {
                              const tools = pInfo.webSearch?.tools ?? [];
                              const mode: WebSearchMode = tools.includes(next as WebSearchTool) ? (next as WebSearchTool) : "off";
                              setWebSearch((w) => ({ ...w, [p.value]: withWebSearchMode(w[p.value], mode) }));
                            }}
                            options={[
                              { value: "off", label: intl.formatMessage({ id: "model.webSearch.off", defaultMessage: "Off" }) },
                              ...pInfo.webSearch.tools.map((tool) => ({
                                value: tool,
                                label:
                                  tool === "web_search_premium"
                                    ? intl.formatMessage({ id: "model.webSearch.premium", defaultMessage: "Premium" })
                                    : intl.formatMessage({ id: "model.webSearch.standard", defaultMessage: "Standard" }),
                              })),
                            ]}
                          />
                          <div className="smycles-field-meta mt-1 text-ink-muted">
                            {intl.formatMessage(
                              {
                                id: "model.webSearch.hint",
                                defaultMessage:
                                  "Research on the public web through {provider}, billed per search. Premium adds verified news sources.",
                              },
                              { provider: p.label },
                            )}
                          </div>
                        </div>
                      ) : (
                        <div data-testid={`model-web-search-${p.value}`}>
                          <div className="smycles-field-label mb-2 mt-4">
                            {intl.formatMessage({ id: "model.webSearch.label", defaultMessage: "Web search" })}
                          </div>
                          <div className="smycles-field-meta text-ink-muted">
                            {intl.formatMessage({
                              id: "model.webSearch.unavailable",
                              defaultMessage: "Not available for this provider.",
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {switching ? (
          <div className="box mt-4 bg-ink/[0.02] p-3 smycles-field-meta text-ink">
            {intl.formatMessage(
              {
                id: "model.providerSwitchHint",
                defaultMessage:
                  "Switching to {provider} changes the embedding model ({from} → {to}). It may be necessary to reprocess the documents again after saving so they stay searchable.",
              },
              {
                provider: PROVIDERS.find((p) => p.value === provider)?.label ?? provider,
                from: embedModelId(savedProvider),
                to: embedModelId(provider),
              },
            )}
          </div>
        ) : null}

        {/* No card-level Save: the edits sit in the app-wide SaveBar (@adr SPARK-0005-unified-deferred-save-bar@1), which also answers for
            them when the reader switches the section or leaves the page. */}
      </SettingsCard>

      {/* Its own pending scope and route: the SaveBar saves it alongside this card, not through it. */}
      <ResearchSourcesCard />

      {/* The documents section keys off the STORED/active provider (savedProvider), not the selected
          tile: uploads embed against the active provider, so availability + the button label must
          reflect what's actually in effect, updating only once a switch is saved. */}
      <KnowledgeDocumentsCard provider={savedProvider} refreshKey={reembedRefresh} uploadBlockedUntil={uploadBlockedUntil ?? undefined} />

      {/* Save failures are shown by whichever surface ran the save (SaveBar, dialog); only the
          confirmation is this card's own, in the slot the SaveBar has just vacated. */}
      {confirmation ? (
        <div className="fixed bottom-4 left-1/2 z-[900] w-[36rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 sm:bottom-8">
          <div className="smycles-toast">
            {/* min-h matches the SaveBar's button-row height so a text-only
                toast is the same pill height as the profile SaveBar. */}
            <span className="flex min-h-6 min-w-0 flex-1 items-center">
              {intl.formatMessage({
                id: "model.saved",
                defaultMessage: "Settings saved – may take up to 30 seconds to take effect.",
              })}
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}
